import type { DocumentHeaderCandidate, DocumentHeaderCandidates } from '@avisoma/shared';

const CATEGORY_ORDER = ['adStatus', 'llpStatus', 'qecList', 'arc'] as const satisfies readonly (keyof DocumentHeaderCandidates)[];

const CATEGORY_LABELS: Record<keyof DocumentHeaderCandidates, string> = {
  adStatus: 'AD Status',
  llpStatus: 'LLP Status',
  qecList: 'QEC / LRU List',
  arc: 'ARC',
};

const FIELD_ORDER = ['esn', 'engineType', 'tsn', 'csn', 'asOfDate'] as const satisfies readonly (keyof DocumentHeaderCandidate)[];

const FIELD_LABELS: Record<keyof DocumentHeaderCandidate, string> = {
  esn: 'ESN',
  engineType: 'Engine Type',
  tsn: 'TSN',
  csn: 'CSN',
  asOfDate: 'As of Date',
};

export type DocumentHeaderGroup = {
  key: keyof DocumentHeaderCandidates;
  label: string;
  fields: [string, string][];
};

/**
 * Flatten present document-header candidates into labelled groups. `0` is a valid
 * TSN/CSN and must render; omitted fields and empty strings do not.
 */
export function presentDocumentHeaderGroups(
  headers: DocumentHeaderCandidates | undefined,
): DocumentHeaderGroup[] {
  if (!headers) return [];

  return CATEGORY_ORDER.flatMap((key) => {
    const candidate = headers[key];
    if (!candidate) return [];

    const fields = FIELD_ORDER.flatMap((field) => {
      const value = candidate[field];
      if (value == null || value === '') return [];
      return [[FIELD_LABELS[field], String(value)] as [string, string]];
    });

    if (fields.length === 0) return [];
    return [{ key, label: CATEGORY_LABELS[key], fields }];
  });
}
